import { useEffect, useState } from "react";
import MiniNavigationTabs from "./miniNavigationTabs";
import MainContentLayout from "../../components/mainContentLayout";
import PageHeader from "../../components/pageHeader";
import LoadingOverlay from "../../components/loadingOverlay";
import ConfirmDeleteModal from "../../components/confirmDeleteModal";
import FilePreviewModal from "../../components/filePreviewModal";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";

export default function PlayerLeavesPage() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [deleteId, setDeleteId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const { showToast, ToastContainer } = useToast();
  const token = sessionStorage.getItem("access_token");

  async function loadLeaves() {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/player/leaves?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) setItems(data.items);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLeaves();
  }, [month, year]);

  async function deleteLeave() {
    const res = await fetch(
      `${API_BASE}/api/player/leave/delete/${deleteId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    const data = await res.json();

    if (data.success) {
      showToast("Leave deleted", "success");
      loadLeaves();
    }
    setDeleteId(null);
  }

  return (
    <MainContentLayout>
      <PageHeader title="My Leaves" backTo="/dashboard" />
      <MiniNavigationTabs />

      {/* Month + Year Filter */}
      <div className="flex mb-4 gap-2">
        <select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="border p-2 rounded"
        >
          <option value={-1}>All Months</option>
          {Array.from({ length: 12 }).map((_, i) => (
            <option key={i} value={i}>
              {new Date(0, i).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border p-2 rounded"
        >
          {Array.from({ length: 6 }).map((_, i) => {
            const y = year - 2 + i;
            return (
              <option key={y} value={y}>
                {y}
              </option>
            );
          })}
        </select>
      </div>

      {/* LIST */}
      <ul className="divide-y">
        {items.length === 0 && (
          <p className="text-gray-600">No leaves found.</p>
        )}

        {items.map((lv, idx) => {
          const s = new Date(lv.startDate);
          const e = new Date(lv.endDate);

          return (
            <li key={lv.id} className="py-3 flex justify-between items-center">
              {/* LEFT SIDE */}
              <div className="text-left">
                <div className="font-semibold">
                  {idx + 1}. {lv.type}
                </div>

                <div className="text-sm text-gray-500">
                  {s.toLocaleDateString()} — {e.toLocaleDateString()}
                </div>

                {lv.activityName && (
                  <div className="text-sm text-gray-600">
                    {lv.activityName}
                  </div>
                )}

                {lv.emergencyReason && (
                  <div className="text-sm text-red-500">
                    Reason: {lv.emergencyReason}
                  </div>
                )}

                {lv.remark && (
                  <div className="text-sm text-gray-500">
                    Remark: {lv.remark}
                  </div>
                )}
              </div>

              {/* RIGHT SIDE ACTIONS */}
              <div className="flex flex-col items-end gap-2">
                {/* VIEW DOC (MC only) */}
                {lv.type === "Medical Leave" && lv.documentUrl && (
                  <button
                    onClick={() => setPreviewUrl(lv.documentUrl)}
                    className="text-blue-600 underline text-sm"
                  >
                    View Doc
                  </button>
                )}

                <button
                  onClick={() => setDeleteId(lv.id)}
                  className="text-red-600 underline text-sm"
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* DELETE MODAL */}
      {deleteId && (
        <ConfirmDeleteModal
          show={true}
          title="Delete Leave?"
          message="Are you sure you want to delete this leave?"
          onCancel={() => setDeleteId(null)}
          onConfirm={deleteLeave}
        />
      )}

      {/* PREVIEW MODAL */}
      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />

      <ToastContainer />
      <LoadingOverlay show={loading} />
    </MainContentLayout>
  );
}
